/**
 * Block 12 PR-C Phase C7 Step 2 — useFreeAnketaPersist.
 *
 * Debounced auto-save (300ms default) from `FreeAnketaState` → IDB `projects`
 * store через `saveProject` (Phase A3). Mirror useAnketaPersist pattern:
 *   - Skip persist если `!state.isDirty && state.projectName === ''`.
 *   - True debounce: каждое state change сбрасывает pending timer; save
 *     fires только после `delayMs` тишины.
 *   - Unmount cleanup: forcePersist через persistNowRef (synchronous save).
 *   - forcePersist() — public API для navigate (КП CTA, deeplinks).
 *
 * **Mapping FreeAnketaState → ProjectRecord:**
 *   - `id` = state.projectId (UUID v4 client-generated)
 *   - `name` = state.projectName или '(без названия)' fallback
 *   - `furnitureType` = 'other' (literal — discriminator для filtering списка)
 *   - `data.params` = `{type:'other'}` (OtherParamsSchema strict — no extra keys)
 *   - `data.itemName` = projectName fallback
 *   - `data.freeItems[]` = freeRows.map(stripId) — CalculatorFreeItem shape
 *   - `data.total` = sum, NaN-guarded (parallel useFreeAnketaForm.total)
 *   - `syncStatus` = 'local' (sync queue Phase A5 promotes на online)
 *
 * Reference: ADR-0020 §Q6 (300ms debounce + forcePersist on unmount), §Q9
 * (separate FreeAnketaForm), ADR-0019 §Q11.2-§Q11.4 (IDB sync states).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { saveProject } from '@/lib/offline/idb/projects';
import type { ProjectRecord } from '@/lib/offline/idb/database';
import type { FreeAnketaState } from './types';

export type PersistStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseFreeAnketaPersistResult {
  persistedAt: number | null;
  persistStatus: PersistStatus;
  /** Skip debounce + sync save. Используется при navigate (КП CTA / unmount). */
  forcePersist: () => Promise<void>;
}

function safeTotal(state: FreeAnketaState): number {
  return state.freeRows.reduce((acc, r) => {
    const subtotal = r.quantity * r.price;
    return acc + (Number.isFinite(subtotal) && subtotal >= 0 ? subtotal : 0);
  }, 0);
}

function stateToRecord(state: FreeAnketaState, userId: string): ProjectRecord {
  const name = state.projectName.trim() || '(без названия)';
  const total = safeTotal(state);
  const freeItems = state.freeRows.map((r) => ({
    name: r.name,
    quantity: Number.isFinite(r.quantity) && r.quantity >= 0 ? r.quantity : 0,
    unit: r.unit,
    price: Number.isFinite(r.price) && r.price >= 0 ? r.price : 0,
  }));
  return {
    id: state.projectId,
    data: {
      itemName: name,
      params: { type: 'other' },
      furnitureType: 'other',
      freeItems,
      total,
    },
    furnitureType: 'other',
    name,
    total,
    syncStatus: 'local',
    createdAt: state.createdAt,
    updatedAt: state.updatedAt,
    userId,
  };
}

function shouldPersist(state: FreeAnketaState): boolean {
  return state.isDirty || state.projectName.trim() !== '';
}

/**
 * @param state — current FreeAnketaState (passed each render from useFreeAnketaForm).
 * @param userId — JWT subject id (Container resolves from auth store). Без real
 *   auth — fallback 'anon-user' (как useAnketaPersist Container pattern).
 * @param delayMs — debounce delay (production 300ms; tests pass smaller value).
 */
export function useFreeAnketaPersist(
  state: FreeAnketaState,
  userId: string,
  delayMs: number = 300,
): UseFreeAnketaPersistResult {
  const [persistedAt, setPersistedAt] = useState<number | null>(null);
  const [persistStatus, setPersistStatus] = useState<PersistStatus>('idle');

  const stateRef = useRef(state);
  const userIdRef = useRef(userId);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const persistNow = useCallback(async () => {
    const snapshot = stateRef.current;
    if (!shouldPersist(snapshot)) return;
    setPersistStatus('saving');
    try {
      await saveProject(stateToRecord(snapshot, userIdRef.current));
      setPersistedAt(Date.now());
      setPersistStatus('saved');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[useFreeAnketaPersist] save failed', err);
      }
      setPersistStatus('error');
    }
  }, []);

  const persistNowRef = useRef(persistNow);
  useEffect(() => {
    persistNowRef.current = persistNow;
  }, [persistNow]);

  // Debounced auto-save — каждое изменение state сбрасывает pending timer.
  useEffect(() => {
    if (!shouldPersist(state)) return;
    const timer = setTimeout(() => {
      void persistNowRef.current();
    }, delayMs);
    return () => clearTimeout(timer);
  }, [state, delayMs]);

  // Unmount safety net — empty deps + ref access делает «runs only on unmount».
  useEffect(() => {
    return () => {
      void persistNowRef.current();
    };
  }, []);

  return { persistedAt, persistStatus, forcePersist: persistNow };
}
